import React, { useState, useContext, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Link } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AuthContext } from '../../../../AuthContext';
import './CreateProject.css';

import { fetchUserOrganisations } from '../../../../apis/organisations.api';
import { getProjExistingUsers, updateProject, fetchProjDetails, getProjUsers } from '../../../../apis/projects.org';
import { fetchUsers, getUserRoles } from '../../../../apis/users.api';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper component
const CustomPopper = styled(Popper)(({ theme }) => ({
  width: 'auto !important',
  minWidth: '45%',
  zIndex: theme.zIndex.modal,  
}));

function EditProject({ 
  openEditProject, 
  setOpenEditProject, 
  setIsToastOpen, 
  setToastMessage, 
  selectedProj, 
  setLoading,
  setErrorCode 
}) {

  const queryClient = useQueryClient();
  const { authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;
  const [orgId, setOrgId] = useState(null);
  const [formValues, setFormValues] = useState(
        {
          "project_name": "",
          "org_name": null,
          "users":[{ pur_Id: null, name: null, role: null }],
        }
      )

  const handleOrganizationChange = (e, value) => {
    setOrgId(value?.org_id);
    setFormValues({ ...formValues, org_name: value?.name });
  };

  const handleAddNewUser = () => {
    setFormValues({ ...formValues, users: [...formValues.users, { pur_Id:null, name: null, role: null }] });
  };

  const handleChange = (index, field, value) => {
    const newUsers = [...formValues.users];
    newUsers[index][field] = value;
    setFormValues((prevValues) => ({
      ...prevValues,
      users: newUsers
    }));
  };

  const handleRemoveUser = (e, index) => {
    const newUsers = formValues?.users.filter((user, i) => i !== index);
    setFormValues({ ...formValues, users: newUsers.length === 0 ? [{ pur_Id:null, name: null, role: null }] : newUsers });
  };

  const { data: orgsData, isLoading: loadingOrg } = useQuery({
    queryKey : ['organisations', { access_token, token_type }], 
    queryFn : fetchUserOrganisations, 
    enabled: !!access_token 
    }
  );

  useEffect(()=>{
    const getProjDetails = async () => {
      const  data = await fetchProjDetails({ queryKey: ["projDetails", {selectedProj,access_token, token_type}]});
      setOrgId(data ? data[0]?.org_id : null);
      setFormValues((prevValues) => ({
        ...prevValues,
        project_name: data[0]?.name,
        org_name: data[0]?.org_name,
      }));

      const projUsers = await getProjExistingUsers({ queryKey: ["projUsers" , {selectedProj, access_token, token_type}]});
      setFormValues((prevValues) => ({
        ...prevValues,
        users: projUsers.length > 0 ? projUsers.map((user) => ({ pur_Id: user.pur_id, name: user.name, role: user.role_name })) : [{ pur_Id: null, name: null, role: null }]
      }));
    }

    getProjDetails();
  },[])
  
  const { data: getUsers, isLoading: loadingUsers } = useQuery({
    queryKey : ['ProjUsers', { orgId, access_token, token_type }],
    queryFn : getProjUsers,
    enabled: !!access_token && !!orgId 
  }
  );

  const { data: getUserProjRoles, isLoading: loadingUserProjRoles } = useQuery({
    queryKey : ['userProjRoles', {role_type:"project_role", access_token, token_type}],
    queryFn : getUserRoles,
    enabled: !!access_token 
  }
  );

  const findUserId = (userName) => {
    const user = getUsers?.find((u) => u.name === userName);
    return user ? user.user_id : 0;
  };

  const findRoleId = (roleName) => {
    const role = getUserProjRoles?.find((r) => r.role_name === roleName);
    return role ? role.role_id : 0;
  };

  const mutation = useMutation({
    mutationFn : updateProject, 
      onSuccess: (res) => {
        queryClient.invalidateQueries('projects');
        setToastMessage('Project has been updated successfully');
        setErrorCode(res?.status);
        setLoading(false);
        setIsToastOpen(true);
      },
      onError: (error) => {
        setLoading(false);
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
        setErrorCode(error?.response?.status);
        setToastMessage(`Error: ${errorMessage}`);
        setIsToastOpen(true);
      }
    });
  
  const EditProjectFormSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: formValues.project_name,
      users: formValues.users.map((user) => ({
        pur_id: user.pur_Id,
        user_id: findUserId(user.name),
        role_id: findRoleId(user.role)
      }))
    };

    setLoading(true);
    setOpenEditProject(false);
    mutation.mutate({ selectedProj, payload, access_token, token_type });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={openEditProject}
      onClose={() => setOpenEditProject(false)}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      className="createProjectModal"
    >
      <Fade in={openEditProject}>
        <Box className="createProjectModalClass">
          <div className="createProjectModalHeader">
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              className="createProjectModalTitle"
            >
              Edit Project
            </Typography>
            <span
              className="createProjectModalClose"
              onClick={() => setOpenEditProject(false)}
            >
              &times;
            </span>
          </div>
          <form className="g-3" onSubmit={EditProjectFormSubmit}>
            <div className="createProjectModalBody">
              <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                <div className="col-6" style={{ paddingRight: '12px' }}>
                  <label htmlFor="org-name" className="form-label">
                    Project Name<span style={{color: 'red'}}>&#65290;</span>
                  </label>
                  <TextField
                    required
                    id="org-name"
                    placeholder="Project name"
                    sx={{
                      width: "100%",
                    }}
                    onChange={(e) => setFormValues({...formValues, project_name: e.target.value})}
                    value={formValues?.project_name}
                  />
                </div>
                <div className="col-6" style={{ paddingRight: '12px' }}>
                  <label htmlFor="org-name" className="form-label">
                    Organization Name<span style={{color: 'red'}}>&#65290;</span>
                  </label>
                  <Autocomplete
                    disablePortal
                    options={!loadingOrg ? orgsData?.filter(org => !org?.is_deleted) : []}
                    getOptionLabel={(option) => option?.name}
                    onChange={handleOrganizationChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        id="org-name"
                        placeholder="Organization name"
                        sx={{ width: "100%" }}
                      />
                    )}
                    value={orgsData?.find(org => org.name === formValues?.org_name) || null}
                  />
                </div>
              </div>
              <div className='superAdminTitle'>Users</div>
              <div style={{ margin: '16px 0' }}>
                {formValues.users?.map((user, index) => (
                  <div key={index} style={{ marginTop: '16px' }}>
                    <Link
                      sx={{
                        display: 'flex',
                        textDecoration: 'none',
                        justifyContent: 'flex-end',
                        marginRight: '12px',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => handleRemoveUser(e, index)}
                    >
                      Remove
                    </Link>
                    <div className="d-flex">
                      <div className="col-6" style={{ paddingRight: '12px' }}>
                        <label htmlFor="username" className="form-label">Username{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                        <Autocomplete
                          disablePortal
                          options={getUsers || []}
                          getOptionLabel={(option) => option?.name}
                          value={getUsers?.find(u => u?.name === user?.name) || null}
                          onChange={(event, newValue) => {
                            handleChange(index, 'name', newValue ? (newValue?.name) : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              placeholder="Enter Username"
                              name="name"
                              onChange={(e) => handleChange(index, 'name', e.target.value)}
                              sx={{ width: "100%" }}
                            />
                          )}
                          PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                          />
                        
                      </div>
                      <div className="col-6" style={{ paddingRight: '12px' }}>
                        <label htmlFor="role" className="form-label">Role{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                        <Autocomplete
                          disablePortal
                          options={getUserProjRoles || []}
                          getOptionLabel={(option) => option?.role_name}
                          value={getUserProjRoles?.find(r => r?.role_name === user?.role) || null}
                          onChange={(event, newValue) => {
                            handleChange(index, 'role', newValue ? (newValue?.role_name) : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              placeholder="Enter role"
                              name="role"
                              onChange={(e) => handleChange(index, 'role', e.target.value)}
                              sx={{ width: "100%" }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAddNewUser}
                  sx={{
                    textTransform: 'capitalize',
                    fontFamily: 'open sans',
                    fontWeight: 400,
                    borderRadius: '0',
                    m: '16px 0'
                  }}
                >
                  Add more
                </Button>
              </div>
            </div>
            <div className="createProjectFormButtons">
              <Button variant="outlined" size="large" onClick={() => setOpenEditProject(false)}>
                Cancel
              </Button>
              <Button variant="contained" size="large" type="submit">Save</Button>
            </div>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
}

export default EditProject;
